import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        if (data?.session_token) {
          localStorage.setItem("gf_session_token", data.session_token);
        }
        // CRITICAL: update AuthProvider state BEFORE navigating,
        // otherwise Protected route sees user=null and bounces back to /
        if (data?.user) setUser(data.user);
        const role = data?.user?.role;
        const userType = (data?.user?.user_type || "").toLowerCase();
        // Clean hash so it doesn't get re-triggered
        window.history.replaceState({}, document.title, window.location.pathname);
        const isAdminRealm = role === "admin" || userType === "staff";
        if (!isAdminRealm) {
          // Customer-realm — portal.
          navigate("/portal", { replace: true });
          return;
        }
        // Staff realm — figure out the first admin page the user has
        // permission to view so a restricted role doesn't land on a
        // forbidden `/dashboard`. Fall back to /dashboard for Super
        // Admin (which has every perm) and legacy admin bootstrap.
        try {
          const { data: me } = await api.get("/rbac/me");
          const perms = new Set(me?.permissions || []);
          const isSA =
            (me?.role_ids || []).includes("role_super_admin") ||
            (role === "admin" && (me?.role_ids || []).length === 0);
          if (isSA) {
            navigate("/dashboard", { replace: true });
            return;
          }
          // Import lazily to avoid a circular reference during initial
          // bundle evaluation.
          const { NAV } = await import("../lib/navMatrix");
          const firstAllowed = NAV.find((n) => {
            if (!n.anyOf || n.anyOf.length === 0) return true; // auth_only
            return n.anyOf.some((code) => perms.has(code));
          });
          navigate(firstAllowed ? firstAllowed.path : "/dashboard", { replace: true });
        } catch (e) {
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        console.error("Auth callback failed", e);
        // Phase 2 (2026-02) — surface the customer-safe login error
        // (unknown / disabled / removed / ambiguous email) back on the
        // login page so the user sees "why". We pass the message via
        // sessionStorage so the message survives the redirect without
        // ending up in the URL bar.
        const detail = e?.response?.data?.detail || "";
        const statusCode = e?.response?.status;
        try {
          if (detail) sessionStorage.setItem("gf_login_error", detail);
        } catch { /* ignore quota errors */ }
        // If it's a portal-side denial, keep them on the customer
        // login page; otherwise send them to /.
        const isPortalDenial = statusCode === 403 && (
          detail.includes("customer account")
          || detail.includes("portal access")
          || detail.includes("customer portal")
        );
        navigate(isPortalDenial ? "/customer/login" : "/", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="text-sm" style={{ fontFamily: "IBM Plex Mono" }}>
        AUTHENTICATING…
      </div>
    </div>
  );
}
